import { createSignal } from 'solid-js'
import { DateTime } from 'luxon'

/**
 * TENANT-4915 Phase 11 — shared, simulated-clock-aware "now" for the web app.
 *
 * Holds a single offset (server simulated time − real wall-clock time, in ms).
 * It is ONLY ever set to a non-zero value in non-prod, by the QA Time-Travel
 * overlay (which reads the server `/dev/clock`). In production the overlay never
 * mounts, so the offset stays `0` and `appNow()` is byte-identical to real time.
 *
 * The offset lives in a Solid signal, so any time-derived value computed inside a
 * tracking scope (component render, `createMemo`, etc.) that calls `appNow()` /
 * `appNowDateTime()` automatically re-derives when QA changes the simulated clock
 * — no page refresh needed (e.g. a booking card flips to "Hosting"/"Checking Out").
 */
const [
    simOffsetMillis,
    setSimOffsetMillisSignal,
] = createSignal(0)

/**
 * When the server clock is frozen, `appNow()` must return the same fixed instant (no
 * ticking). A simple offset would still drift with `Date.now()`. This signal holds the
 * frozen epoch-ms value; when non-null, `appNow()` / `appNowDateTime()` return it directly.
 */
const [
    frozenAtMillis,
    setFrozenAtMillisSignal,
] = createSignal(null)

/** Last server-reported sim-clock state id (`X-Lodgr-Sim-Clock-State`). */
const [
    knownSimClockStateId,
    setKnownSimClockStateIdSignal,
] = createSignal(null)

/** True when an API response echoed a different sim-clock state than the client assumes. */
const [
    simClockStale,
    setSimClockStaleSignal,
] = createSignal(false)

/**
 * Re-entrant guard marking a window in which the tester is themselves mutating the
 * shared sim clock (set / +-1hr / +-1day / set-from-timeline / reset via the QA overlay).
 *
 * The server computes a NEW `stateId` the instant the clock changes, so the very
 * response to the mutation request — and any requests in flight while it lands —
 * legitimately carry a different id than the client's last-known baseline. Without
 * this guard the drift check would read that self-induced change as EXTERNAL drift
 * and raise a false "de-sync" warning. While the guard is held, responses are
 * adopted as the new baseline instead of being flagged. A genuine external change
 * (another tab/user, server restart, TTL expiry) happens OUTSIDE this window and
 * still flags correctly. A counter (not a boolean) tolerates overlapping mutations.
 */
let localClockMutationDepth = 0

/** Enter a user-initiated clock-mutation window (see `localClockMutationDepth`). */
export const beginLocalClockMutation = () => {
    localClockMutationDepth += 1
}

/** Leave a user-initiated clock-mutation window; never drops below zero. */
export const endLocalClockMutation = () => {
    localClockMutationDepth = Math.max(0, localClockMutationDepth - 1)
}

/** True while the tester's own clock mutation is in flight. */
export const isLocalClockMutationInProgress = () => localClockMutationDepth > 0

/** Reactive getter for the current simulated-clock offset in ms (0 = real time). */
export const getSimOffsetMillis = simOffsetMillis

/** Reactive getter for the last-known server sim-clock state id. */
export const getKnownSimClockStateId = knownSimClockStateId

/** Reactive getter — true when server sim time diverged from the local assumption. */
export const isSimClockStale = simClockStale

/** @param {string|null|undefined} stateId */
export const setKnownSimClockStateId = (stateId) => {
    setKnownSimClockStateIdSignal(stateId || null)
}

/** @param {boolean} stale */
export const setSimClockStale = (stale) => {
    setSimClockStaleSignal(Boolean(stale))
}

export const clearSimClockStale = () => {
    setSimClockStaleSignal(false)
}

/**
 * Adopt a server state id and clear any stale flag (e.g. after `/dev/clock` sync).
 * @param {string} stateId
 */
export const adoptSimClockStateId = (stateId) => {
    if (!stateId) {
        throw new Error('adoptSimClockStateId: stateId is required')
    }
    setKnownSimClockStateId(stateId)
    clearSimClockStale()
}

/** @param {number} milliseconds offset (server sim time − real time). Non-finite resets to 0. */
export const setSimOffsetMillis = (milliseconds) =>
    setSimOffsetMillisSignal(Number.isFinite(milliseconds) ? milliseconds : 0)

/** Clear any simulated offset and frozen pin — `appNow()` returns to real wall-clock time. */
export const clearSimOffset = () => {
    setSimOffsetMillisSignal(0)
    setFrozenAtMillisSignal(null)
}

/**
 * Derive + store the offset from a server `effectiveNow` ISO instant. Both clocks
 * tick at the same rate, so the offset captured here stays accurate until the
 * simulated clock is changed again.
 *
 * When `frozen` is true, the web stores a fixed epoch-ms value instead of an offset,
 * so `appNow()` returns the exact same instant on every call (no ticking).
 *
 * @param {string|null|undefined} effectiveNowIso
 * @param {{ frozen?: boolean }} [options]
 */
export const syncSimClockFromEffectiveNow = (effectiveNowIso, options) => {
    if (!effectiveNowIso) {
        clearSimOffset()
        clearFrozenAt()
        return
    }
    const parsed = DateTime.fromISO(effectiveNowIso)
    if (!parsed.isValid) {
        clearSimOffset()
        clearFrozenAt()
        return
    }
    if (options?.frozen) {
        setFrozenAt(parsed.toMillis())
        setSimOffsetMillis(parsed.toMillis() - Date.now())
    } else {
        clearFrozenAt()
        setSimOffsetMillis(parsed.toMillis() - Date.now())
    }
}

/** Current effective time as a JS `Date` (reactive — subscribes to the sim offset). */
export const appNow = () => {
    const frozen = frozenAtMillis()
    if (frozen !== null) return new Date(frozen)
    return new Date(Date.now() + simOffsetMillis())
}

/** Current effective time as a Luxon `DateTime` (reactive — subscribes to the sim offset). */
export const appNowDateTime = () => {
    const frozen = frozenAtMillis()
    if (frozen !== null) return DateTime.fromMillis(frozen)
    return DateTime.now().plus({
        milliseconds: simOffsetMillis(),
    })
}

/**
 * Real wall-clock epoch ms — the reference the simulated offset is measured against. Not "now"
 * for the app (that is `appNow()` / `appNowDateTime()`); only for measuring sim-vs-real gaps.
 */
export const realNowMillis = () => Date.now()

/**
 * How far the app clock is from real wall-clock time, in ms (positive = ahead).
 * Unfrozen this is the stored offset; frozen, real time keeps moving past the pinned instant,
 * so the gap is measured against the real "now" the caller passes. For the QA sim clock offset readout.
 * @param {number} realNowEpochMillis real wall-clock time, epoch ms (callers pass `realNowMillis()`)
 */
export const appClockOffsetMillis = (realNowEpochMillis) => {
    if (!Number.isFinite(realNowEpochMillis)) {
        throw new TypeError('appClockOffsetMillis: realNowEpochMillis must be a finite number')
    }
    const frozen = frozenAtMillis()
    if (frozen !== null) return frozen - realNowEpochMillis
    return simOffsetMillis()
}

/** True when a non-zero simulated offset is in effect (i.e. QA is time-travelling). */
export const isSimClockActive = () => simOffsetMillis() !== 0 || frozenAtMillis() !== null

/** Reactive getter — true when the web clock is frozen (fixed instant, no ticking). */
export const isClockFrozen = () => frozenAtMillis() !== null

/** @param {number} millis Epoch-ms to freeze at. */
export const setFrozenAt = (millis) =>
    setFrozenAtMillisSignal(Number.isFinite(millis) ? millis : null)

/** Clear the frozen pin — `appNow()` resumes ticking with the offset. */
export const clearFrozenAt = () => setFrozenAtMillisSignal(null)
